import { useEffect, useRef } from 'react'
import http from '../http'
import { AxiosError } from 'axios'

// Shared refresh state - единый механизм для всего приложения
let sharedRefreshPromise: Promise<string | undefined> | null = null

/**
 * Выполняет refresh токена с защитой от параллельных вызовов.
 * Можно использовать из любого места (HTTP interceptor, WebSocket и т.д.)
 */
export const executeTokenRefresh = async (
  refreshFn: () => Promise<string | undefined>
): Promise<string | undefined> => {
  if (sharedRefreshPromise) {
    return sharedRefreshPromise
  }

  sharedRefreshPromise = refreshFn()

  try {
    const token = await sharedRefreshPromise
    return token
  } finally {
    sharedRefreshPromise = null
  }
}

/**
 * Проверяет, является ли ошибка Network Error, который может быть вызван
 * истёкшим токеном (например, CORS preflight упал из-за 401)
 */
const isNetworkErrorPossiblyFromExpiredToken = (error: AxiosError): boolean => {
  return (
    !error.response &&
    error.code === 'ERR_NETWORK' &&
    !!error.config?.headers?.Authorization
  )
}

export const useHttpInterceptorEffect = ({
  refreshToken,
  onError,
}: {
  refreshToken: () => Promise<string | undefined>
  onError: ({
    status,
  }: {
    status: 'no_access' | 'token_invalid' | 'network_error'
  }) => void
}) => {
  const retryCountRef = useRef<Map<string, number>>(new Map())
  const MAX_RETRIES = 2

  useEffect(() => {
    // Response interceptor
    const interceptor = http.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config

        if (!originalRequest) {
          return Promise.reject(error)
        }

        const requestKey = `${originalRequest.method}-${originalRequest.url}`
        const currentRetries = retryCountRef.current.get(requestKey) || 0

        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
          console.log('401', error.response.data)

          const errorMessage =
            (
              error.response.data as { message?: string }
            )?.message?.toLowerCase() || ''

          if (errorMessage === 'no access') {
            onError({ status: 'no_access' })
            retryCountRef.current.delete(requestKey)
            return Promise.reject(error)
          }

          if (currentRetries >= MAX_RETRIES) {
            console.log('Max retries reached for 401')
            onError({ status: 'token_invalid' })
            retryCountRef.current.delete(requestKey)
            return Promise.reject(error)
          }

          retryCountRef.current.set(requestKey, currentRetries + 1)

          try {
            const token = await executeTokenRefresh(refreshToken)

            if (!token) {
              console.log('Token refresh returned null')
              onError({ status: 'token_invalid' })
              retryCountRef.current.delete(requestKey)
              return Promise.reject(error)
            }

            http.config({ token })
            originalRequest.headers.Authorization = `Bearer ${token}`

            const result = await http(originalRequest)
            retryCountRef.current.delete(requestKey)
            return result
          } catch (refreshError) {
            console.log('Token refresh error:', refreshError)
            onError({ status: 'token_invalid' })
            retryCountRef.current.delete(requestKey)
            return Promise.reject(refreshError)
          }
        }

        // Handle Network Error - может быть вызван истёкшим токеном
        if (isNetworkErrorPossiblyFromExpiredToken(error)) {
          console.log(
            'Network error with Authorization header, attempting token refresh'
          )

          if (currentRetries >= MAX_RETRIES) {
            console.log('Max retries reached for network error')
            onError({ status: 'network_error' })
            retryCountRef.current.delete(requestKey)
            return Promise.reject(error)
          }

          retryCountRef.current.set(requestKey, currentRetries + 1)

          try {
            const token = await executeTokenRefresh(refreshToken)

            if (!token) {
              console.log('Token refresh returned null on network error')
              onError({ status: 'token_invalid' })
              retryCountRef.current.delete(requestKey)
              return Promise.reject(error)
            }

            http.config({ token })
            originalRequest.headers.Authorization = `Bearer ${token}`

            const result = await http(originalRequest)
            retryCountRef.current.delete(requestKey)
            return result
          } catch (refreshError) {
            console.log('Token refresh error on network error:', refreshError)
            onError({ status: 'network_error' })
            retryCountRef.current.delete(requestKey)
            return Promise.reject(refreshError)
          }
        }

        return Promise.reject(error)
      }
    )

    // Cleanup
    return () => {
      http.interceptors.response.eject(interceptor)
      retryCountRef.current.clear()
    }
  }, [refreshToken, onError])
}
